import axios, { CancelTokenSource } from 'axios'
import { HospitalAdditionStatusTypes } from 'models/HospitalAdditionStatus.model'

import { Hospital } from '../../models/Hospital.model'
import { HospitalDTO, HospitalUpdatePayload } from '../../models/HospitalDTO.model'
import { PaginatedResponse } from '../../models/PaginatedResponse.model'
import { USStates } from '../../models/USStates.model'
import { getObjectKeyFromValue } from '../../utils'
import { getApiErrorMessage } from '../../utils/apiError'
import ScrubstrApiQueryService, {
  ScrubstrApiQueryServiceGetEvent,
} from '../ScrubstrApiQueryService/ScrubstrApiQueryService'

export class HospitalService {
  private autocompleteHospitalsSource: CancelTokenSource | null = null

  private formatHospital(hospitalRaw: HospitalDTO): Hospital {
    return {
      address: hospitalRaw.address,
      city: hospitalRaw.city,
      claimStatus: hospitalRaw.claimStatus,
      createdAt: hospitalRaw.createdAt,
      customizationStatus: hospitalRaw.customizationStatus ?? null,
      id: hospitalRaw.id,
      image: hospitalRaw.imageURL,
      mostCommonChartingSoftwareAnswer: hospitalRaw.mostCommonChartingSoftwareAnswer,
      mostCommonScrubColorAnswer: hospitalRaw.mostCommonScrubColorAnswer,
      name: hospitalRaw.name,
      permalink: hospitalRaw.permalink,
      rating: hospitalRaw.averageRating,
      ratingsSummary: hospitalRaw.ratingsSummary,
      reviewCount: hospitalRaw.reviewCount,
      reviews: hospitalRaw.reviews,
      state: {
        code: hospitalRaw.state,
        name: getObjectKeyFromValue(USStates, hospitalRaw.state),
      },
      supportEmail: hospitalRaw.supportEmail,
      supportTelephone: hospitalRaw.supportTelephone,
      updatedAt: hospitalRaw.updatedAt,
      website: hospitalRaw.website,
      zip: hospitalRaw.zipcode,
    }
  }

  /**
   * @description Search for a single hospital by id or permalink
   *
   * @param id
   * @param permalink
   *
   * @returns promise for a Hospital
   */
  public async searchOne({ id, permalink }: { id?: number; permalink?: string }): Promise<Hospital> {
    const params = {
      id,
      permalink,
    }
    const scrubstrApiQueryRequest: ScrubstrApiQueryServiceGetEvent = {
      endpoint: `hospital/search-one`,
      params,
    }

    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    const hospitalRaw = await scrubstrApiQueryService.get<HospitalDTO>(scrubstrApiQueryRequest)
    return this.formatHospital(hospitalRaw)
  }

  public async update(id: number, payload: HospitalUpdatePayload): Promise<Hospital> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    const hospitalRaw = await scrubstrApiQueryService.patch<HospitalDTO>({
      data: payload,
      endpoint: `hospital/${id}`,
    })
    return this.formatHospital(hospitalRaw)
  }

  public async uploadLogo(id: number, file: File): Promise<Hospital> {
    const formData = new FormData()
    formData.append('logo', file)

    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    const hospitalRaw = await scrubstrApiQueryService.post<HospitalDTO>({
      data: formData,
      endpoint: `hospital/${id}/logo`,
      headers: {},
    })
    return this.formatHospital(hospitalRaw)
  }

  /**
   * @description Add a new hospital
   *
   * @param data - Hospital data to add
   *
   * @returns promise for the created Hospital DTO
   */
  public async add(data: {
    name: string
    city: string
    state: string
    submissionNotes?: string
    newHospitalStatus?: string
  }): Promise<HospitalDTO> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()

    try {
      const hospital = await scrubstrApiQueryService.post<HospitalDTO>({
        data: {
          city: data.city,
          name: data.name,
          newHospitalStatus: data.newHospitalStatus || HospitalAdditionStatusTypes.PENDING,
          state: data.state,
          submissionNotes: data.submissionNotes,
        },
        endpoint: 'hospital',
      })

      return hospital
    } catch (error) {
      console.error('Error adding hospital:', error)
      throw new Error(getApiErrorMessage(error, 'Sorry, there was a problem adding the hospital.'))
    }
  }
  public async search({
    page,
    pageSize,
    term,
    state,
    city,
    sort,
    autocomplete,
    minRating,
  }: {
    page?: number
    pageSize?: number
    term: string
    state?: USStates
    city?: string
    sort: string
    autocomplete?: boolean
    minRating?: number
  }): Promise<{
    hospitals: Hospital[]
    totalResults: number
    totalPages: number
    currentPage: number
  }> {
    const params: {
      page?: number
      pageSize?: number
      name?: string
      state?: USStates
      city?: string
      orderBy?: string
      orderDirection?: string
      orderAToZTies?: string
      excludeReviews?: string
      minRating?: number
    } = {
      city,
      excludeReviews: autocomplete ? 'true' : undefined,
      minRating: minRating,
      name: term || undefined,
      page,
      pageSize,
      state,
    }

    if (sort) {
      switch (sort) {
        case 'newest':
          params.orderBy = 'latestReviewDate'
          params.orderDirection = 'DESC'
          params.orderAToZTies = 'true'
          break
        case 'mostReviewed':
          params.orderBy = 'reviewCount'
          params.orderDirection = 'DESC'
          params.minRating = minRating
          params.orderAToZTies = 'true'
          break
        case 'leastReviewed':
          params.orderBy = 'reviewCount'
          params.orderDirection = 'ASC'
          params.orderAToZTies = 'true'
          break
        case 'highRating':
          params.orderBy = 'averageRating'
          params.orderDirection = 'DESC'
          params.minRating = minRating
          params.orderAToZTies = 'true'
          break
        case 'lowRating':
          params.orderBy = 'averageRating'
          params.orderDirection = 'ASC'
          params.orderAToZTies = 'true'
          break
        case 'az':
          params.orderBy = 'name'
          params.orderDirection = 'ASC'
          break
        case 'za':
          params.orderBy = 'name'
          params.orderDirection = 'DESC'
          break
        default:
          break
      }
    }

    const scrubstrApiQueryRequest: ScrubstrApiQueryServiceGetEvent = {
      endpoint: 'hospital',
      params,
    }

    if (autocomplete) {
      if (this.autocompleteHospitalsSource !== null) {
        this.autocompleteHospitalsSource.cancel('cancelToken triggered')
      }
      this.autocompleteHospitalsSource = axios.CancelToken.source()
      scrubstrApiQueryRequest.cancelToken = this.autocompleteHospitalsSource.token
    }

    const hospitalResults: {
      hospitals: Hospital[]
      totalResults: number
      totalPages: number
      currentPage: number
    } = {
      currentPage: Number(page),
      hospitals: [],
      totalPages: 0,
      totalResults: 0,
    }
    try {
      const scrubstrApiQueryService = new ScrubstrApiQueryService()
      const hospitalsRaw = await scrubstrApiQueryService.get<PaginatedResponse<HospitalDTO>>(scrubstrApiQueryRequest)
      // wrap to avoid errors if the request is cancelled
      if (hospitalsRaw) {
        hospitalResults.hospitals = hospitalsRaw.data.map((hospital) => this.formatHospital(hospital))

        hospitalResults.totalResults = hospitalsRaw.total
        hospitalResults.totalPages = Math.ceil(hospitalsRaw.total / 12)
      }
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }

    return hospitalResults
  }

  public async searchCityState(params: { searchTerm: string; limit?: number }) {
    const scrubstrApiQueryRequest: ScrubstrApiQueryServiceGetEvent = {
      endpoint: 'hospital/search-city-state',
      params,
    }

    if (this.autocompleteHospitalsSource !== null) {
      this.autocompleteHospitalsSource.cancel('cancelToken triggered')
    }
    this.autocompleteHospitalsSource = axios.CancelToken.source()
    scrubstrApiQueryRequest.cancelToken = this.autocompleteHospitalsSource.token

    try {
      const scrubstrApiQueryService = new ScrubstrApiQueryService()
      return await scrubstrApiQueryService.get<{ city: string; state: USStates; stateName: string }[]>(
        scrubstrApiQueryRequest,
      )
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }
  }
}
